import React from "react";
import { Link } from "react-router-dom";

/**
 * AISOBlock — clean, AI-readable content section optimised for ChatGPT,
 * Gemini, Google AI Overviews and Perplexity entity extraction.
 *
 * Renders three structured sub-blocks:
 *   - "Who This Is For"          → audience bullets
 *   - "What We Actually Do"      → plain-language scope paragraph
 *   - "Typical Project Scope"    → clean bullet list
 * Plus an optional "Why Time Furniture" trust strip and a
 * cross-linking row of related services.
 *
 * Designed so AI search can lift the content directly into an
 * answer — short sentences, no marketing waffle, semantic HTML.
 */
export default function AISOBlock({
  testid = "aiso-block",
  audience = [],
  whatWeDo,
  scope = [],
  trustPoints = [
    "UAE since 1993",
    "In-house manufacturing",
    "Turnkey execution",
    "Premium materials",
    "End-to-end responsibility",
  ],
  relatedLinks = [],
  audienceHeading = "Built for clients with real, large-scale projects.",
  bgClass = "bg-[#F2E9D8] border-y border-[#6E1F2C]/15",
}) {
  return (
    <section data-testid={testid} className={bgClass}>
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Who This Is For */}
          <div className="lg:col-span-4">
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-5">
              Who This Is For
            </p>
            <h3 className="font-serif-display text-2xl md:text-[28px] leading-[1.15] text-[#1C110C] mb-6">
              {audienceHeading}
            </h3>
            <ul
              data-testid={`${testid}-audience`}
              className="space-y-3 text-[#3E2A22] leading-[1.7] text-base"
            >
              {audience.map((a, i) => (
                <li
                  key={i}
                  data-testid={`${testid}-audience-${i}`}
                  className="flex items-baseline gap-3"
                >
                  <span
                    aria-hidden
                    className="text-[#D4A963] text-sm leading-none translate-y-[1px]"
                  >
                    ◦
                  </span>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* What We Actually Do */}
          <div className="lg:col-span-4">
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-5">
              What We Actually Do
            </p>
            <h3 className="font-serif-display text-2xl md:text-[28px] leading-[1.15] text-[#1C110C] mb-6">
              Designed, built and executed under one roof.
            </h3>
            <p
              data-testid={`${testid}-what-we-do`}
              className="text-[#3E2A22] leading-[1.85] text-base md:text-[17px]"
            >
              {whatWeDo}
            </p>
            {trustPoints?.length > 0 && (
              <ul
                data-testid={`${testid}-trust`}
                className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-[11px] tracking-[0.18em] uppercase text-[#7A6B5F]"
              >
                {trustPoints.map((p, i) => (
                  <li
                    key={i}
                    data-testid={`${testid}-trust-${i}`}
                    className="flex items-baseline gap-2"
                  >
                    <span aria-hidden className="text-[#D4A963]">·</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Typical Project Scope Includes */}
          <div className="lg:col-span-4">
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-5">
              Typical Project Scope Includes
            </p>
            <h3 className="font-serif-display text-2xl md:text-[28px] leading-[1.15] text-[#1C110C] mb-6">
              One contract — no missing trades.
            </h3>
            <ul
              data-testid={`${testid}-scope`}
              className="divide-y divide-[#6E1F2C]/15 border-y border-[#6E1F2C]/15"
            >
              {scope.map((s, i) => (
                <li
                  key={i}
                  data-testid={`${testid}-scope-${i}`}
                  className="py-3 text-[#1C110C] text-sm md:text-base flex items-baseline gap-3"
                >
                  <span
                    aria-hidden
                    className="text-[#D4A963] text-xs translate-y-[-1px] tracking-widest"
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Cross-link row — explicit, AI-crawlable internal links */}
        {relatedLinks.length > 0 && (
          <div
            data-testid={`${testid}-related`}
            className="mt-14 md:mt-16 pt-10 border-t border-[#6E1F2C]/15"
          >
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-5">
              Related Services
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              {relatedLinks.map((r, i) => (
                <Link
                  key={r.to}
                  to={r.to}
                  data-testid={`${testid}-related-${i}`}
                  className="group bg-[#FAF5EC] border border-[#6E1F2C]/15 hover:border-[#6E1F2C]/45 px-5 py-5 transition-colors"
                >
                  <p className="text-[10px] tracking-[0.28em] uppercase text-[#D4A963]">
                    {r.kicker || "Explore"}
                  </p>
                  <p className="mt-2 font-serif-display text-base md:text-lg text-[#1C110C] leading-snug group-hover:text-[#6E1F2C] transition-colors">
                    {r.label}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
